import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "../lib/api";
import { PageHeader, KpiCard, DataTable, StatusBadge } from "../components/Shell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DatePicker } from "../components/DatePicker";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Archive, ArrowCounterClockwise, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DancerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [dancer, setDancer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [showBooking, setShowBooking] = useState(false);
  const [showAccount, setShowAccount] = useState(false);

  const load = async () => {
    const [d, b, a, r, p] = await Promise.all([
      api.get(`/dancers/${id}`),
      api.get(`/dancers/${id}/bookings`),
      api.get(`/dancers/${id}/accounts`),
      api.get(`/payment-requests`),
      api.get(`/payments`),
    ]);
    setDancer(d.data);
    setBookings(b.data);
    setAccounts(a.data);
    setRequests(r.data.filter((x) => x.danseuse_id === id));
    setPayments(p.data.filter((x) => x.danseuse_id === id));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const activeBooking = bookings.find((b) => b.statut === "actif");

  // Column definitions — memoized so DataTable doesn't re-render when a
  // sibling tab re-renders. Hooks must be called unconditionally (before
  // any early-return) per the Rules of Hooks.
  const bookingsColumns = useMemo(() => [
    { title: "Début", render: (b) => fmtDate(b.date_debut) },
    { title: "Fin prévue", render: (b) => fmtDate(b.date_fin_prevue) },
    { title: "Fin réelle", render: (b) => fmtDate(b.date_fin_reelle) },
    { title: "Logement", render: (b) => (b.logement_fourni ? `${b.loyer_hebdomadaire}€/sem` : "-") },
    { title: "Jours lgt", align: "right", key: "nombre_jours_logement" },
    { title: "Déduc. lgt", align: "right", render: (b) => fmtMoney(b.montant_logement_deduit) },
    { title: "Statut", render: (b) => <StatusBadge status={b.statut} /> },
    {
      title: "",
      render: (b) =>
        b.statut === "actif" ? (
          <Button
            data-testid={`close-booking-${b.id}`}
            size="sm"
            variant="outline"
            onClick={async () => {
              const d = prompt("Date de fin réelle (YYYY-MM-DD)", new Date().toISOString().slice(0, 10));
              if (!d) return;
              await api.post(`/bookings/${b.id}/close`, { date_fin_reelle: d });
              toast.success("Booking clôturé");
              load();
            }}
            className="h-8 border-[#27272a] hover:bg-[#1a1a21] text-white"
          >
            Clôturer
          </Button>
        ) : null,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const accountsColumns = useMemo(() => [
    { title: "Date", render: (a) => fmtDate(a.date_compte) },
    { title: "Billets", align: "right", render: (a) => <span style={{ color: "#34d399" }}>{a.billets}</span> },
    { title: "Jaunes", align: "right", render: (a) => <span style={{ color: "#fbbf24" }}>{a.jetons_jaunes}</span> },
    { title: "Rouges", align: "right", render: (a) => <span style={{ color: "#f87171" }}>{a.jetons_rouges}</span> },
    { title: "Bleus", align: "right", render: (a) => <span style={{ color: "#60a5fa" }}>{a.jetons_bleus}</span> },
    { title: "Acompte", align: "right", render: (a) => fmtMoney(a.acompte) },
    { title: "Total dû", align: "right", render: (a) => fmtMoney(a.total_du_ligne) },
    { title: "Net", align: "right", render: (a) => <b>{fmtMoney(a.total_net_apres_acompte)}</b> },
    { title: "Validation", render: (a) => <StatusBadge status={a.statut_validation} /> },
    {
      title: "",
      render: (a) =>
        a.statut_validation !== "valide" ? (
          <Button
            data-testid={`validate-account-${a.id}`}
            size="sm"
            variant="outline"
            className="h-8 border-[#27272a] hover:bg-[#1a1a21] text-white"
            onClick={async () => {
              await api.post(`/accounts/${a.id}/validate`, { statut_validation: "valide" });
              toast.success("Validé");
              load();
            }}
          >
            <CheckCircle size={14} className="mr-1" /> Valider
          </Button>
        ) : null,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const requestsColumns = useMemo(() => [
    { title: "Date", render: (r) => fmtDate(r.date_demande) },
    { title: "Facture", key: "numero_facture" },
    { title: "Montant", align: "right", render: (r) => fmtMoney(r.montant_calcule) },
    { title: "Statut", render: (r) => <StatusBadge status={r.statut} /> },
  ], []);

  const paymentsColumns = useMemo(() => [
    { title: "Date", render: (p) => fmtDate(p.date_reglement) },
    { title: "Montant", align: "right", render: (p) => fmtMoney(p.montant) },
    { title: "Mode", key: "mode_reglement" },
    { title: "Référence", key: "reference" },
  ], []);

  if (!dancer) return <div className="text-[#a1a1aa]">Chargement...</div>;

  return (
    <div>
      <PageHeader
        testId="dancer-detail-header"
        title={dancer.nom_scene}
        subtitle={`${dancer.prenom} ${dancer.nom} · ${dancer.telephone}`}
        actions={
          <div className="flex gap-2">
            {dancer.statut === "active" ? (
              <Button
                data-testid="archive-dancer"
                variant="outline"
                onClick={async () => {
                  await api.post(`/dancers/${id}/archive`);
                  toast.success("Archivée");
                  load();
                }}
                className="border-[#27272a] hover:bg-[#1a1a21] text-white"
              >
                <Archive size={16} className="mr-2" /> Archiver
              </Button>
            ) : (
              <Button
                data-testid="reactivate-dancer"
                onClick={async () => {
                  await api.post(`/dancers/${id}/reactivate`);
                  toast.success("Réactivée");
                  load();
                }}
                className="bg-[#34d399] hover:bg-[#10b981] text-black"
              >
                <ArrowCounterClockwise size={16} className="mr-2" /> Réactiver
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <KpiCard label="Total brut" value={fmtMoney(dancer.total_brut)} />
        <KpiCard label="Total net généré" value={fmtMoney(dancer.total_net_genere)} tone="jaunes" />
        <KpiCard label="Logement déduit" value={fmtMoney(dancer.total_logement)} />
        <KpiCard label="Total payé" value={fmtMoney(dancer.total_paye)} tone="success" />
        <KpiCard label="Solde disponible" value={fmtMoney(dancer.solde_disponible)} tone={dancer.solde_disponible > 0 ? "success" : "default"} />
      </div>

      <Tabs defaultValue="profil">
        <TabsList className="bg-[#121217] border border-[#27272a]">
          <TabsTrigger value="profil" data-testid="tab-profil">Profil</TabsTrigger>
          <TabsTrigger value="booking" data-testid="tab-booking">Booking</TabsTrigger>
          <TabsTrigger value="comptes" data-testid="tab-comptes">Comptes</TabsTrigger>
          <TabsTrigger value="demandes" data-testid="tab-demandes">Demandes</TabsTrigger>
          <TabsTrigger value="paiements" data-testid="tab-paiements">Paiements</TabsTrigger>
        </TabsList>

        <TabsContent value="profil" className="mt-6">
          <div className="surface p-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            {[
              ["Nom", dancer.nom],
              ["Prénom", dancer.prenom],
              ["Nom de scène", dancer.nom_scene],
              ["Téléphone", dancer.telephone],
              ["Email", dancer.email || "-"],
              ["SIRET", dancer.siret || "-"],
              ["Raison sociale", dancer.raison_sociale_siret || "-"],
              ["Titulaire RIB", dancer.titulaire_rib || "-"],
              ["IBAN", dancer.iban || "-"],
              ["Date création", fmtDate(dancer.date_creation)],
            ].map(([k, v]) => (
              <div key={k} className="flex items-start gap-3 border-b border-[#27272a] pb-3">
                <div className="label-xs w-32 shrink-0 pt-0.5">{k}</div>
                <div className="text-white">{v}</div>
              </div>
            ))}
            {dancer.notes && (
              <div className="col-span-2 border-t border-[#27272a] pt-3">
                <div className="label-xs mb-2">Notes</div>
                <div className="text-white">{dancer.notes}</div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="booking" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button
              data-testid="new-booking"
              onClick={() => setShowBooking(true)}
              disabled={!!activeBooking}
              className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black disabled:opacity-50"
            >
              <Plus size={16} className="mr-2" /> Ouvrir un booking
            </Button>
          </div>
          <DataTable
            columns={bookingsColumns}
            rows={bookings}
            rowKey="id"
          />
        </TabsContent>

        <TabsContent value="comptes" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button
              data-testid="new-account"
              disabled={!activeBooking}
              onClick={() => setShowAccount(true)}
              className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black disabled:opacity-50"
            >
              <Plus size={16} className="mr-2" /> Nouveau compte
            </Button>
          </div>
          <DataTable
            columns={accountsColumns}
            rows={accounts}
            rowKey="id"
          />
        </TabsContent>

        <TabsContent value="demandes" className="mt-6">
          <DataTable
            columns={requestsColumns}
            rows={requests}
            rowKey="id"
          />
        </TabsContent>

        <TabsContent value="paiements" className="mt-6">
          <DataTable
            columns={paymentsColumns}
            rows={payments}
            rowKey="id"
          />
        </TabsContent>
      </Tabs>

      <BookingDialog open={showBooking} onClose={() => setShowBooking(false)} dancerId={id} onDone={load} />
      {activeBooking && (
        <AccountDialog
          open={showAccount}
          onClose={() => setShowAccount(false)}
          dancerId={id}
          bookingId={activeBooking.id}
          onDone={load}
        />
      )}
    </div>
  );
}

function BookingDialog({ open, onClose, dancerId, onDone }) {
  const [f, setF] = useState({
    date_debut: new Date().toISOString().slice(0, 10),
    date_fin_prevue: "",
    commentaire: "",
    logement_fourni: false,
    loyer_hebdomadaire: 120,
    date_arrivee_appartement: "",
    date_fin_logement: "",
  });
  const submit = async () => {
    try {
      await api.post(`/dancers/${dancerId}/bookings`, f);
      toast.success("Booking créé");
      onClose();
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#121217] border-[#27272a] text-white max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Ouvrir un booking</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-xs">Date début</Label>
              <DatePicker value={f.date_debut} onChange={(v) => setF({ ...f, date_debut: v })} testId="bk-date-debut" />
            </div>
            <div>
              <Label className="label-xs">Date fin prévue</Label>
              <DatePicker value={f.date_fin_prevue} onChange={(v) => setF({ ...f, date_fin_prevue: v })} />
            </div>
          </div>
          <div className="flex items-center gap-3 py-2">
            <Switch data-testid="logement-switch" checked={f.logement_fourni} onCheckedChange={(v) => setF({ ...f, logement_fourni: v })} />
            <div>
              <div className="text-sm">Logement fourni par le club</div>
              <div className="text-xs text-[#71717a]">Calcul automatique au prorata</div>
            </div>
          </div>
          {f.logement_fourni && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-xs">Loyer hebdo (100-130€)</Label>
                <Input type="number" min="100" max="130" value={f.loyer_hebdomadaire} onChange={(e) => setF({ ...f, loyer_hebdomadaire: Number(e.target.value) })} className="bg-[#09090b] border-[#27272a]" />
              </div>
              <div>
                <Label className="label-xs">Arrivée appartement</Label>
                <DatePicker value={f.date_arrivee_appartement} onChange={(v) => setF({ ...f, date_arrivee_appartement: v })} />
              </div>
            </div>
          )}
          <Textarea placeholder="Commentaire" value={f.commentaire} onChange={(e) => setF({ ...f, commentaire: e.target.value })} className="bg-[#09090b] border-[#27272a]" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#27272a] text-white hover:bg-[#1a1a21]">Annuler</Button>
          <Button data-testid="submit-booking" onClick={submit} className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black">Créer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountDialog({ open, onClose, dancerId, bookingId, onDone }) {
  const [f, setF] = useState({
    date_compte: new Date().toISOString().slice(0, 10),
    billets: 0,
    jetons_jaunes: 0,
    jetons_rouges: 0,
    jetons_bleus: 0,
    acompte: 0,
    commentaire: "",
  });
  const partBillets = f.billets * 1 * 0.45;
  const partJaunes = f.jetons_jaunes * 20 * 0.45;
  const mRouges = f.jetons_rouges * 10;
  const mBleus = f.jetons_bleus * 5;
  const total = partBillets + partJaunes + mRouges + mBleus;
  const net = total - f.acompte;

  const submit = async () => {
    try {
      await api.post(`/dancers/${dancerId}/accounts`, { ...f, booking_id: bookingId });
      toast.success("Compte créé");
      onClose();
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#121217] border-[#27272a] text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Nouveau compte journalier</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-xs">Date</Label>
            <DatePicker value={f.date_compte} onChange={(v) => setF({ ...f, date_compte: v })} testId="acc-date" />
          </div>
          <div className="grid grid-cols-4 gap-4">
            {[
              ["billets", "Billets (1€×45%)", "#34d399"],
              ["jetons_jaunes", "Jaunes (20€×45%)", "#fbbf24"],
              ["jetons_rouges", "Rouges (10€)", "#f87171"],
              ["jetons_bleus", "Bleus (5€)", "#60a5fa"],
            ].map(([k, l, c]) => (
              <div key={k}>
                <Label className="label-xs" style={{ color: c }}>{l}</Label>
                <Input
                  data-testid={`acc-${k}`}
                  type="number"
                  min="0"
                  value={f[k]}
                  onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })}
                  className="bg-[#09090b] border-[#27272a] font-mono"
                />
              </div>
            ))}
          </div>
          <div>
            <Label className="label-xs">Acompte perçu (€)</Label>
            <Input data-testid="acc-acompte" type="number" min="0" value={f.acompte} onChange={(e) => setF({ ...f, acompte: Number(e.target.value) })} className="bg-[#09090b] border-[#27272a] font-mono" />
          </div>
          <Textarea placeholder="Commentaire" value={f.commentaire} onChange={(e) => setF({ ...f, commentaire: e.target.value })} className="bg-[#09090b] border-[#27272a]" />

          <div className="surface p-4 grid grid-cols-5 gap-2 text-sm">
            <Detail label="Part billets" value={fmtMoney(partBillets)} color="#34d399" />
            <Detail label="Part jaunes" value={fmtMoney(partJaunes)} color="#fbbf24" />
            <Detail label="Rouges" value={fmtMoney(mRouges)} color="#f87171" />
            <Detail label="Bleus" value={fmtMoney(mBleus)} color="#60a5fa" />
            <Detail label="Total dû" value={fmtMoney(total)} color="#fff" />
          </div>
          <div className="flex justify-between items-center surface p-3">
            <span className="label-xs">Net après acompte</span>
            <span data-testid="net-preview" className="font-display text-2xl" style={{ color: "#fbbf24" }}>
              {fmtMoney(net)}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#27272a] text-white hover:bg-[#1a1a21]">Annuler</Button>
          <Button data-testid="submit-account" onClick={submit} className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, value, color }) {
  return (
    <div>
      <div className="label-xs">{label}</div>
      <div className="font-mono text-sm mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
